import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useCreateGame, useRoster } from "@/lib/queries";
import { ApiError } from "@/lib/api";
import { useStickyState } from "@/lib/useStickyState";
import { NEW_GAME_KEYS } from "@/lib/newGameSticky";
import type { Player } from "@/lib/types";

// Sticky-key namespace owned by lib/newGameSticky.ts so "Play Again"
// callers (Game Detail, virtual endgame card, etc.) can seed the
// same keys without duplicating string constants. The form remembers
// format + players + target score so a second game opens prefilled.
// Date defaults to TODAY every mount — not sticky.

export default function NewGame() {
  const [, setLocation] = useLocation();
  const { data: players = [], isLoading: rosterLoading } = useRoster();
  const create = useCreateGame();

  const [format, setFormat] = useStickyState<"singles" | "doubles">(
    NEW_GAME_KEYS.format,
    "singles",
  );
  const today = new Date().toISOString().slice(0, 10);
  const [playedOn, setPlayedOn] = useState(today);
  const [team1Ids, setTeam1Ids] = useStickyState<string[]>(
    NEW_GAME_KEYS.team1,
    [],
  );
  const [team2Ids, setTeam2Ids] = useStickyState<string[]>(
    NEW_GAME_KEYS.team2,
    [],
  );
  const [targetScore, setTarget] = useStickyState<number>(
    NEW_GAME_KEYS.target,
    21,
  );
  const [error, setError] = useState<string | null>(null);

  // Prune restored team IDs against the current roster. A player
  // might have been deleted between sessions; without this the form
  // would carry a phantom selection that the dropdown can't display
  // and the server would reject ("player_not_in_org"). Runs once
  // roster has loaded; the filter is a no-op when nothing's stale.
  useEffect(() => {
    if (rosterLoading) return;
    const valid = new Set(players.map((p) => p.id));
    const t1 = team1Ids.filter((id) => valid.has(id));
    const t2 = team2Ids.filter((id) => valid.has(id));
    if (t1.length !== team1Ids.length) setTeam1Ids(t1);
    if (t2.length !== team2Ids.length) setTeam2Ids(t2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rosterLoading, players]);

  const expected = format === "singles" ? 1 : 2;
  const ready =
    team1Ids.length === expected &&
    team2Ids.length === expected &&
    !team1Ids.some((id) => team2Ids.includes(id));

  async function onSubmit() {
    setError(null);
    try {
      const game = await create.mutateAsync({
        playedOn,
        format,
        scoringMethod: "both_sides",
        team1PlayerIds: team1Ids,
        team2PlayerIds: team2Ids,
        targetScore,
      });
      setLocation(`/score/${game.shareCode}`);
    } catch (e) {
      setError((e as ApiError).message ?? "Failed to create game");
    }
  }

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-10 max-w-3xl mx-auto space-y-6">
      <header className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display uppercase tracking-tight">
            New game
          </h1>
          <p className="text-muted-foreground text-sm">
            Score every hand, with a live spectator link.
          </p>
        </div>
        <Button asChild variant="outline" size="sm">
          <Link href="/dashboard/players">
            <UserPlus size={14} /> Roster
          </Link>
        </Button>
      </header>

      {players.length === 0 && (
        <Card>
          <CardContent className="p-4 text-sm text-muted-foreground flex items-center justify-between gap-3">
            <span>You have no players in your roster yet.</span>
            <Button asChild size="sm">
              <Link href="/dashboard/players">Add players →</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Match details</CardTitle>
          <CardDescription>Players and format.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Date</label>
              <Input
                type="date"
                value={playedOn}
                onChange={(e) => setPlayedOn(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Format</label>
              <div className="grid grid-cols-2 gap-2">
                {(["singles", "doubles"] as const).map((f) => (
                  <button
                    key={f}
                    type="button"
                    className={`h-11 rounded-md border text-sm font-medium ${
                      format === f
                        ? "border-primary bg-primary/5"
                        : "border-border hover:bg-muted"
                    }`}
                    onClick={() => {
                      setFormat(f);
                      setTeam1Ids([]);
                      setTeam2Ids([]);
                    }}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <TeamPicker
              label="Team 1"
              players={players}
              expected={expected}
              selected={team1Ids}
              excluded={team2Ids}
              onChange={setTeam1Ids}
              accent="primary"
            />
            <TeamPicker
              label="Team 2"
              players={players}
              expected={expected}
              selected={team2Ids}
              excluded={team1Ids}
              onChange={setTeam2Ids}
              accent="secondary"
            />
          </div>

          <div className="grid grid-cols-3 gap-2">
            <label className="col-span-3 text-sm font-medium">Target score</label>
            {[11, 21].map((n) => (
              <button
                key={n}
                type="button"
                className={`h-11 rounded-md border text-sm font-medium ${
                  targetScore === n
                    ? "border-primary bg-primary/5"
                    : "border-border hover:bg-muted"
                }`}
                onClick={() => setTarget(n)}
              >
                {n}
              </button>
            ))}
            <input
              type="number"
              value={targetScore}
              min={7}
              max={99}
              className="h-11 rounded-md border border-border text-sm px-3 bg-background"
              onChange={(e) => setTarget(Number(e.target.value) || 21)}
            />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <Button
            size="lg"
            className="w-full"
            disabled={!ready || create.isPending}
            onClick={onSubmit}
          >
            {create.isPending ? "Saving…" : "Start scoring"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function TeamPicker({
  label,
  players,
  expected,
  selected,
  excluded,
  onChange,
  accent,
}: {
  label: string;
  players: Player[];
  expected: number;
  selected: string[];
  excluded: string[];
  onChange: (ids: string[]) => void;
  accent: "primary" | "secondary";
}) {
  return (
    <div className="space-y-3 rounded-lg border border-border p-3 relative">
      <div
        className={`absolute -top-2.5 left-3 px-2 text-xs font-display uppercase tracking-wider bg-card ${
          accent === "primary" ? "text-primary" : "text-secondary"
        }`}
      >
        {label}
      </div>
      {Array.from({ length: expected }).map((_, i) => {
        const value = selected[i] ?? "";
        return (
          <select
            key={i}
            value={value}
            onChange={(e) => {
              const next = [...selected];
              if (e.target.value) next[i] = e.target.value;
              else next.splice(i, 1);
              onChange(next.filter(Boolean));
            }}
            className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="">Pick a player…</option>
            {players
              .filter((p) => !excluded.includes(p.id))
              .filter((p) => !selected.includes(p.id) || p.id === value)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
          </select>
        );
      })}
    </div>
  );
}
